/**
 * Per-publication notification settings — lean single-column form.
 *
 * Route: /notifications/targets/:targetId
 *
 * Sections (top-to-bottom):
 *   1. Sender identity (from address + display name)
 *   2. Test mode (toggle + greyed-out recipient input when off)
 *   3. Sending caps (per-recipient/day, per-site/day, min confidence)
 *   4. First-touch review toggle
 *   5. Mailjet Subscribers list id (read-only outreach list id + editable subscriber list id)
 *   6. Branding (logo + 2 colours, advanced fields collapsed)
 *   7. Legal footer (legal name + ABN — postal address dropped per operator decision)
 */

import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Save, ArrowLeft, ExternalLink, AlertTriangle, ChevronDown, ChevronRight } from 'lucide-react';
import {
  getTargetNotificationSettings,
  updateTargetNotificationSettings,
  type TargetSettingsPatch,
} from '../lib/api/notifications.js';

const DEFAULT_LEGAL = {
  legal_name: 'Vyklow Analytics Pty Ltd',
  abn: '22 688 212 795',
};

export default function TargetNotificationSettingsPage() {
  const { targetId } = useParams();
  const navigate = useNavigate();
  if (!targetId) return <div className="p-6">No target_id in route</div>;
  return <Editor targetId={targetId} onBack={() => navigate(-1)} />;
}

function Editor({ targetId, onBack }: { targetId: string; onBack: () => void }) {
  const qc = useQueryClient();

  const { data, isLoading, error } = useQuery({
    queryKey: ['notifications', 'target-settings', targetId],
    queryFn: () => getTargetNotificationSettings(targetId),
  });

  const [form, setForm] = useState<TargetSettingsPatch | null>(null);
  const [dirty, setDirty] = useState(false);
  const [advancedOpen, setAdvancedOpen] = useState(false);

  useEffect(() => {
    if (!data || form) return;
    const cfg = data.config ?? {};
    setForm({
      notification_from_email: data.notification_from_email,
      notification_from_name: data.notification_from_name,
      config_patch: {
        notification_daily_cap: cfg.notification_daily_cap ?? 25,
        notification_min_confidence: cfg.notification_min_confidence ?? 0.85,
        notification_max_per_recipient_per_day:
          (cfg as { notification_max_per_recipient_per_day?: number }).notification_max_per_recipient_per_day ?? 1,
        notification_require_first_touch_review:
          cfg.notification_require_first_touch_review !== false,
        test_mode_enabled: cfg.test_mode_enabled === true,
        test_mode_recipient: cfg.test_mode_recipient || '',
        email_branding: cfg.email_branding || {},
        legal: {
          legal_name: cfg.legal?.legal_name || DEFAULT_LEGAL.legal_name,
          abn: cfg.legal?.abn || DEFAULT_LEGAL.abn,
        },
        mailjet_subscriber_list_id: cfg.mailjet_subscriber_list_id ?? undefined,
      },
    });
  }, [data, form]);

  const saveMutation = useMutation({
    mutationFn: (patch: TargetSettingsPatch) => updateTargetNotificationSettings(targetId, patch),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['notifications', 'target-settings', targetId] });
      qc.invalidateQueries({ queryKey: ['notifications', 'targets-overview'] });
      setDirty(false);
    },
  });

  const update = (path: string[], value: unknown) => {
    setDirty(true);
    setForm(prev => {
      if (!prev) return prev;
      const next: TargetSettingsPatch = JSON.parse(JSON.stringify(prev));
      let cursor: Record<string, unknown> = next as unknown as Record<string, unknown>;
      for (let i = 0; i < path.length - 1; i++) {
        const k = path[i];
        if (!cursor[k] || typeof cursor[k] !== 'object') cursor[k] = {};
        cursor = cursor[k] as Record<string, unknown>;
      }
      cursor[path[path.length - 1]] = value;
      return next;
    });
  };

  if (isLoading) return <div className="p-4 text-sm text-grey-500">Loading…</div>;
  if (error || !data) {
    return <div className="p-4 text-sm text-danger-700">Failed to load: {(error as Error)?.message}</div>;
  }

  const recents = data.config?.test_mode_recent_recipients ?? [];
  const subscriberListId = data.config?.mailjet_subscriber_list_id;
  const outreachListId = data.config?.mailjet_outreach_list_id;
  const testOn = form?.config_patch?.test_mode_enabled === true;

  return (
    <div className="max-w-3xl mx-auto p-6">
      <button onClick={onBack} className="inline-flex items-center gap-1 text-sm text-grey-600 hover:text-grey-900 mb-2">
        <ArrowLeft className="h-4 w-4" /> Back
      </button>

      <header className="mb-5">
        <h1 className="text-xl font-semibold text-grey-900">{data.target_name} · notification settings</h1>
      </header>

      {!form ? null : (
        <div className="space-y-5">
          {/* Sender */}
          <Block title="Sender">
            <Row label="From / Reply-To">
              <input
                type="email"
                value={form.notification_from_email ?? ''}
                onChange={e => update(['notification_from_email'], e.target.value)}
                placeholder={`feedback@${data.config?.publish_domain || 'example.com'}`}
                className="input font-mono"
              />
            </Row>
            <Row label="Display name">
              <input
                type="text"
                value={form.notification_from_name ?? ''}
                onChange={e => update(['notification_from_name'], e.target.value)}
                placeholder={`${data.target_name} Editorial`}
                className="input"
              />
            </Row>
          </Block>

          {/* Test mode — slider with greyed-out input when off */}
          <Block
            title="Test mode"
            highlight={testOn ? 'warning' : undefined}
            subtitle={
              testOn
                ? 'Sends redirect to the tester address. Real recipients are not contacted; Mailjet list operations skipped.'
                : 'Sends go to real recipients.'
            }
          >
            <Row label="Test mode">
              <Toggle
                checked={testOn}
                onChange={v => update(['config_patch', 'test_mode_enabled'], v)}
                onLabel="On — sends redirect to tester"
                offLabel="Off — sends go to real recipients"
              />
            </Row>
            <Row label="Tester address">
              <input
                type="email"
                list={`recents-${targetId}`}
                value={form.config_patch?.test_mode_recipient ?? ''}
                onChange={e => update(['config_patch', 'test_mode_recipient'], e.target.value.toLowerCase().trim())}
                placeholder="you@vyklow.com"
                disabled={!testOn}
                className={`input font-mono ${!testOn ? 'opacity-50 cursor-not-allowed bg-grey-50' : ''}`}
              />
              {recents.length > 0 && (
                <datalist id={`recents-${targetId}`}>
                  {recents.map(r => <option key={r} value={r} />)}
                </datalist>
              )}
              {testOn && recents.length > 0 && (
                <div className="mt-1 text-xs text-grey-500">
                  Recent: {recents.slice(0, 5).map(r => (
                    <button
                      key={r}
                      type="button"
                      onClick={() => update(['config_patch', 'test_mode_recipient'], r)}
                      className="font-mono text-primary-700 hover:underline mr-2"
                    >
                      {r}
                    </button>
                  ))}
                </div>
              )}
            </Row>
          </Block>

          {/* Sending caps */}
          <Block title="Caps">
            <Row label="Per recipient / day" hint="Maximum sends to the same email address on this publication in one day. Default 1 — most journalists shouldn't be emailed twice for separate articles in the same day.">
              <input
                type="number"
                min="0"
                value={form.config_patch?.notification_max_per_recipient_per_day ?? 1}
                onChange={e => update(['config_patch', 'notification_max_per_recipient_per_day'], Number(e.target.value))}
                className="input w-24 font-mono"
              />
            </Row>
            <Row label="Per publication / day" hint="Maximum sends across all recipients on this publication in one day. Warmup ramp cap.">
              <input
                type="number"
                min="0"
                value={form.config_patch?.notification_daily_cap ?? 25}
                onChange={e => update(['config_patch', 'notification_daily_cap'], Number(e.target.value))}
                className="input w-24 font-mono"
              />
            </Row>
            <Row label="Min extraction confidence" hint="Recipients below this Prism-extraction confidence get excluded by default.">
              <input
                type="number"
                min="0"
                max="1"
                step="0.05"
                value={form.config_patch?.notification_min_confidence ?? 0.85}
                onChange={e => update(['config_patch', 'notification_min_confidence'], Number(e.target.value))}
                className="input w-24 font-mono"
              />
            </Row>
            <Row label="First-touch review" hint="When on, first send to a new (recipient, entity) pair queues for explicit approval.">
              <Toggle
                checked={form.config_patch?.notification_require_first_touch_review !== false}
                onChange={v => update(['config_patch', 'notification_require_first_touch_review'], v)}
                onLabel="On"
                offLabel="Off"
              />
            </Row>
          </Block>

          {/* Mailjet lists */}
          <Block title="Mailjet lists" subtitle="Editorial Outreach list is auto-created on first send. Subscribers list comes from the pub site's signup form.">
            <Row label="Editorial Outreach list id">
              <span className="font-mono text-sm text-grey-700">
                {outreachListId ?? <em className="text-grey-400">auto-creates on first send</em>}
              </span>
            </Row>
            <Row label="Subscribers list id">
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  value={form.config_patch?.mailjet_subscriber_list_id ?? ''}
                  onChange={e => {
                    const v = e.target.value;
                    update(['config_patch', 'mailjet_subscriber_list_id'], v ? Number(v) : undefined);
                  }}
                  placeholder="12345"
                  className="input w-32 font-mono"
                />
                {subscriberListId && (
                  <a
                    href={`https://app.mailjet.com/contacts/lists/show/${subscriberListId}`}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1 text-xs text-primary-700 hover:underline"
                  >
                    View <ExternalLink className="h-3 w-3" />
                  </a>
                )}
                {subscriberListId && (
                  <Link to="/notifications/subscribers" className="text-xs text-primary-700 hover:underline">
                    Subscribers panel →
                  </Link>
                )}
              </div>
            </Row>
          </Block>

          {/* Legal footer */}
          <Block title="Legal footer" subtitle="Appears at the foot of every notification email.">
            <Row label="Entity name">
              <input
                type="text"
                value={form.config_patch?.legal?.legal_name ?? DEFAULT_LEGAL.legal_name}
                onChange={e => update(['config_patch', 'legal', 'legal_name'], e.target.value)}
                className="input"
              />
            </Row>
            <Row label="ABN">
              <input
                type="text"
                value={form.config_patch?.legal?.abn ?? DEFAULT_LEGAL.abn}
                onChange={e => update(['config_patch', 'legal', 'abn'], e.target.value)}
                className="input font-mono"
              />
            </Row>
          </Block>

          {/* Branding — collapsed by default */}
          <Block title="Branding">
            <button
              type="button"
              onClick={() => setAdvancedOpen(v => !v)}
              className="inline-flex items-center gap-1 text-sm text-grey-700 hover:text-grey-900"
            >
              {advancedOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
              {advancedOpen ? 'Hide branding fields' : 'Customise branding (logo, colours, font)'}
            </button>
            {advancedOpen && (
              <div className="mt-3 space-y-2">
                <Row label="Logo URL">
                  <input
                    type="url"
                    value={form.config_patch?.email_branding?.logo_url ?? ''}
                    onChange={e => update(['config_patch', 'email_branding', 'logo_url'], e.target.value)}
                    placeholder={`https://${data.config?.publish_domain || 'example.com'}/assets/logo-email.png`}
                    className="input font-mono text-xs"
                  />
                </Row>
                <Row label="Primary colour">
                  <input
                    type="text"
                    value={form.config_patch?.email_branding?.primary_color ?? ''}
                    onChange={e => update(['config_patch', 'email_branding', 'primary_color'], e.target.value)}
                    placeholder="#0a3d62"
                    className="input w-32 font-mono"
                  />
                </Row>
                <Row label="Accent colour">
                  <input
                    type="text"
                    value={form.config_patch?.email_branding?.accent_color ?? ''}
                    onChange={e => update(['config_patch', 'email_branding', 'accent_color'], e.target.value)}
                    placeholder="#e58e26"
                    className="input w-32 font-mono"
                  />
                </Row>
                <Row label="Font family">
                  <input
                    type="text"
                    value={form.config_patch?.email_branding?.font_family ?? ''}
                    onChange={e => update(['config_patch', 'email_branding', 'font_family'], e.target.value)}
                    placeholder="Georgia, 'Times New Roman', serif"
                    className="input font-mono text-xs"
                  />
                </Row>
                <Row label="Footer brand line">
                  <input
                    type="text"
                    value={form.config_patch?.email_branding?.footer_brand_line ?? ''}
                    onChange={e => update(['config_patch', 'email_branding', 'footer_brand_line'], e.target.value)}
                    placeholder={`${data.target_name} — Vyklow Analytics`}
                    className="input"
                  />
                </Row>
              </div>
            )}
          </Block>

          {/* Save bar */}
          <div className="sticky bottom-0 -mx-6 px-6 py-3 bg-surface border-t border-grey-200 flex items-center justify-between">
            <div className="text-xs text-grey-500">
              {dirty
                ? <span className="text-warning-700 font-medium">Unsaved changes</span>
                : saveMutation.isSuccess ? <span className="text-success-700">Saved</span> : 'No unsaved changes'}
            </div>
            <div className="flex items-center gap-2">
              {saveMutation.isError && (
                <span className="text-xs text-danger-700">
                  Save failed: {(saveMutation.error as Error).message}
                </span>
              )}
              <button
                onClick={() => form && saveMutation.mutate(form)}
                disabled={!dirty || saveMutation.isPending}
                className="px-3 py-1.5 text-sm font-medium rounded text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50 inline-flex items-center gap-1.5"
              >
                <Save className="h-4 w-4" />
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        .input {
          width: 100%;
          padding: 0.4rem 0.6rem;
          font-size: 0.875rem;
          border: 1px solid #d0d4d9;
          border-radius: 0.375rem;
          background: #fff;
        }
        .input:focus { outline: none; border-color: #0a3d62; }
        .input:disabled { cursor: not-allowed; }
      `}</style>
    </div>
  );
}

// ---------------------------------------------------------------------
// Small primitives
// ---------------------------------------------------------------------

function Block({
  title, subtitle, children, highlight,
}: {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
  highlight?: 'warning';
}) {
  return (
    <section className={`rounded-md border px-4 py-3 ${
      highlight === 'warning'
        ? 'border-warning-200 bg-warning-50/60'
        : 'border-grey-200 bg-surface'
    }`}>
      <div className="flex items-center gap-1.5 mb-2">
        {highlight === 'warning' && <AlertTriangle className="h-4 w-4 text-warning-600" />}
        <h2 className="text-sm font-semibold text-grey-900">{title}</h2>
      </div>
      {subtitle && <p className="text-xs text-grey-600 mb-2 -mt-1">{subtitle}</p>}
      <div className="space-y-2">{children}</div>
    </section>
  );
}

function Row({
  label, hint, children,
}: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <div className="grid grid-cols-[180px_1fr] gap-x-4 gap-y-0.5 items-start py-1">
      <label className="text-xs font-medium text-grey-700 mt-1.5">{label}</label>
      <div>
        {children}
        {hint && <p className="text-xs text-grey-500 mt-0.5 leading-snug">{hint}</p>}
      </div>
    </div>
  );
}

function Toggle({
  checked, onChange, onLabel, offLabel,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  onLabel?: string;
  offLabel?: string;
}) {
  return (
    <label className="inline-flex items-center gap-2 cursor-pointer">
      <input
        type="checkbox"
        checked={checked}
        onChange={e => onChange(e.target.checked)}
        className="h-5 w-9 appearance-none rounded-full bg-grey-300 checked:bg-success-600 transition-colors relative cursor-pointer
                   before:content-[''] before:absolute before:top-0.5 before:left-0.5 before:h-4 before:w-4 before:rounded-full before:bg-surface before:transition-transform
                   checked:before:translate-x-4"
      />
      <span className="text-sm text-grey-700">
        {checked ? (onLabel ?? 'On') : (offLabel ?? 'Off')}
      </span>
    </label>
  );
}
